import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { MasternodeWithStats } from '../../hooks/useMasternodes';
import MasternodeListHeader from './MasternodeListHeader';
import MasternodeRow from './MasternodeRow';
import MasternodeCard from './MasternodeCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface MasternodeListProps {
  nodes: MasternodeWithStats[];
  loading: boolean;
  error: Error | null;
}

const INITIAL_DISPLAY_COUNT = 5;

export default function MasternodeList({ nodes, loading, error }: MasternodeListProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const displayedNodes = isExpanded ? nodes : nodes.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreNodes = nodes.length > INITIAL_DISPLAY_COUNT;

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-green-500/10">
        <div className="p-6 animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-700/50 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-green-500/10">
        <div className="text-red-400 text-center">
          Failed to load masternode data
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        {displayedNodes.map((node, index) => (
          <MasternodeCard key={`${node.addr}-${index}`} node={node} />
        ))}
        {hasMoreNodes && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mx-auto flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors duration-200 py-3"
          >
            {isExpanded ? (
              <>
                <span>Show Less</span>
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                <span>Show {nodes.length - INITIAL_DISPLAY_COUNT} More</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-green-500/10">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <MasternodeListHeader />
          </thead>
          <tbody>
            {displayedNodes.map((node, index) => (
              <MasternodeRow key={`${node.addr}-${index}`} node={node} />
            ))}
          </tbody>
        </table>
      </div>
      {hasMoreNodes && (
        <div className="border-t border-green-500/10 p-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mx-auto flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors duration-200"
          >
            {isExpanded ? (
              <>
                <span>Show Less</span>
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                <span>Show All {nodes.length} Masternodes</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}